import { defineStore } from "pinia";
import axiosInstance from "@/services/AxiosTokenInstance";

export const useCompanyStore = defineStore("company", {
  state: () => ({
    // Currently selected company object
    selectedCompany: JSON.parse(localStorage.getItem("selectedCompany")),
    // Roles for the CURRENTLY SELECTED company only
    // These are company-scoped roles (e.g., ROLE_PROJECT_ADMIN, ROLE_COMPANY_ADMIN for this specific company)
    keyUserTypes: JSON.parse(localStorage.getItem("keyUserTypes")) || [],
    // Trigger to refresh companies list
    refreshTrigger: 0,
  }),

  actions: {
    /**
     * Select a company and set roles for that company
     * @param {Object} company - Company object with id and keyUsers array
     */
    selectCompany(company) {
      this.selectedCompany = company;
      localStorage.setItem("selectedCompany", JSON.stringify(company));

      // Load roles for this company
      if (company && company.keyUsers) {
        this.loadCompanyRoles(company);
      }
    },

    /**
     * Extract and set roles for current user from company's keyUsers
     * @param {Object} company - Company object with keyUsers array
     */
    loadCompanyRoles(company) {
      if (!company || !company.keyUsers) {
        this.keyUserTypes = [];
        localStorage.setItem("keyUserTypes", JSON.stringify([]));
        return;
      }

      // Import auth store dynamically to avoid circular dependency
      import("@/stores").then((m) => {
        const authStore = m.useAuthStore();
        const userId = authStore.user?.user_id;

        if (!userId) {
          this.keyUserTypes = [];
          localStorage.setItem("keyUserTypes", JSON.stringify([]));
          return;
        }

        // Extract roles for current user in this company
        const roles = company.keyUsers
          .filter((keyUser) => keyUser.user?.id === userId)
          .map((keyUser) => keyUser.keyUserType);

        this.keyUserTypes = roles;
        localStorage.setItem("keyUserTypes", JSON.stringify(roles));
      });
    },

    /**
     * Set key user types directly (used when company is selected and roles are already extracted)
     * @param {string[]} keyUserTypes - Array of role strings for current company
     */
    selectKeyUserType(keyUserTypes) {
      this.keyUserTypes = keyUserTypes;
      localStorage.setItem("keyUserTypes", JSON.stringify(keyUserTypes));
    },

    /**
     * Fetch company roles from API for a specific company
     * Useful when company object doesn't include keyUsers
     * @param {number} companyId - Company ID
     * @param {number} userId - User ID
     */
    async fetchCompanyRoles(companyId, userId) {
      // Try both numeric and string approaches
      try {
        // First try: Direct URL with numeric IDs
        const url1 = `key_users?user=${userId}&company=${companyId}`;

        let response = await axiosInstance.get(url1);

        // If empty, try with IRI format
        if (
          !response.data["hydra:member"] ||
          response.data["hydra:member"].length === 0
        ) {
          const url2 = `key_users?user=/api/users/${userId}&company=/api/ous/${companyId}`;
          response = await axiosInstance.get(url2);
        }

        const roles = response.data["hydra:member"].map(
          (keyUser) => keyUser.keyUserType,
        );

        // If this is the currently selected company, update roles
        if (this.selectedCompany?.id === companyId) {
          this.keyUserTypes = roles;
          localStorage.setItem("keyUserTypes", JSON.stringify(roles));
        } else {
          console.warn("⚠️ Not setting roles - company mismatch!", {
            selectedCompanyId: this.selectedCompany?.id,
            requestedCompanyId: companyId,
          });
        }

        return roles;
      } catch (error) {
        console.error("Error fetching company roles:", error);
        return [];
      }
    },

    /**
     * Clear all company data (useful on logout)
     */
    clearCompanyData() {
      this.selectedCompany = null;
      this.keyUserTypes = [];
      localStorage.removeItem("selectedCompany");
      localStorage.removeItem("keyUserTypes");
    },

    /**
     * Trigger refresh of companies list
     * Call this after creating, updating, or deleting a company
     */
    triggerRefresh() {
      this.refreshTrigger++;
    },
  },
});
